import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Alert } from 'react-bootstrap';
import { FadeTable, TableLink } from '../Global/Transitions';
import { LoaderIcon } from '../Global/Icons';
import { getRetests } from '../../Services/PtaasApiService';
import { Retest } from '../../Models/Retest';
import { isRetestEligibleProjectStatus } from '../../Utilities/AccessControl';
import RetestRow from './Rows/RetestRow';
// Named for the screen it was written for, but the props are generic (total/page/pageSize) and
// this is the only pagination control in the app. Duplicating it would be worse.
import UsersPagination from '../Users/UsersPagination';

interface Props {
	projectId: string;
	projectStatus: string;
	// Canopy's project id, used only to link a finding through to its full report entry. Absent
	// on a project that never reached Canopy, in which case the titles are plain text.
	canopyId?: number;
	pageSize?: number;
}

const COLUMNS = 5;

export default function RetestList({ projectId, projectStatus, canopyId, pageSize = 5 }: Props) {
	const navigate = useNavigate();
	const { instance, accounts } = useMsal();
	const [retests, setRetests] = useState<Retest[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	// Starts true, not false: the fetch runs on mount, and until it lands an empty `retests` is
	// "not asked yet", not "none". Rendering the empty state and the Request Retest action against
	// that would tell the client there are no retests and offer them a new one — while a retest
	// may in fact already be open.
	const [loading, setLoading] = useState(true);

	// A retest only makes sense once a report exists. This governs whether a retest can be
	// REQUESTED — not whether the table shows. Accepting a retest quote moves the parent project
	// to a delivery status, which is deliberately not eligible, so gating the table on it too
	// would hide the retest the moment its quote is accepted.
	const canRequest = isRetestEligibleProjectStatus(projectStatus);

	useEffect(() => {
		if (accounts.length === 0) return;
		// Fetched regardless of status: a retest whose quote has been accepted sits behind a
		// parent project that has left the eligible status, so whether a retest exists cannot be
		// inferred from the status any more — it has to be asked.
		getRetests(instance, accounts[0], projectId)
			.then((result) => {
				setRetests(result);
				setError(null);
			})
			// getRetests throws `new Error(await response.text())`, so an error with an empty body
			// yields an empty message. Falling back keeps `error` truthy — otherwise the empty
			// state renders and the Request action re-appears on a failed fetch.
			.catch((e) => setError(e.message || 'Retest history could not be loaded.'))
			.finally(() => setLoading(false));
	}, [projectId, instance, accounts]);

	const openRetest = retests.find(
		(r) =>
			r.status === 'Quote Requested' ||
			r.status === 'Quote Available' ||
			r.status === 'In Progress'
	);

	// Ordered so the load failure wins: not knowing whether a retest is already open is not the
	// same as knowing there isn't one, and offering the action either way would let a client
	// raise a second retest against an open one.
	const unavailableReason = error
		? 'A retest cannot be requested right now: the retest history could not be loaded, so we cannot confirm whether one is already in progress.'
		: openRetest
		? 'A retest is already in progress for this project.'
		: !canRequest
		? 'A retest can be requested once the report has been published.'
		: undefined;

	// The Canopy id goes with it when we have one. The wizard builds its own way back out from its
	// route params, so dropping it here strips the phase list and View Reports off the project
	// page the client lands on after submitting, and leaves Crumbs unable to finish the trail.
	const requestPath = `/projects/${projectId}${canopyId != null ? `/${canopyId}` : ''}/retest`;

	const paged = useMemo(() => {
		const start = (page - 1) * pageSize;
		return retests.slice(start, start + pageSize);
	}, [retests, page, pageSize]);

	return (
		<FadeTable>
			<thead>
				<tr>
					<th colSpan={COLUMNS}><h2>Retests</h2></th>
				</tr>
			</thead>
			<thead className='bg-lighter'>
				<tr>
					<th>#</th>
					<th>Requested</th>
					<th>Outcome</th>
					<th>Status</th>
					<th />
				</tr>
			</thead>
			<tbody>
				{/* The retest read goes to Cosmos and can take a moment. The table header is already
				    painted by then, so without this the client is looking at an empty Retests table
				    with no sign anything is happening. */}
				{loading ? (
					<tr>
						<td colSpan={COLUMNS} className='py-4'>
							{/* LoaderIcon is decorative markup with no accessible name, and here it is the
							    only thing in the table — so the wrapper carries the announcement rather
							    than leaving a screen reader with an empty Retests table. */}
							<div role='status' aria-label='Loading retests'>
								<LoaderIcon />
							</div>
						</td>
					</tr>
				) : error ? (
					<tr>
						<td colSpan={COLUMNS}>
							<Alert variant='danger' className='mb-0'>{error}</Alert>
						</td>
					</tr>
				) : paged.length === 0 ? (
					<tr>
						<td colSpan={COLUMNS} style={{ textAlign: 'center', padding: '20px' }}>
							No retests available.
						</td>
					</tr>
				) : (
					paged.map((retest) => (
						<RetestRow key={retest.id} retest={retest} projectId={projectId} canopyId={canopyId} />
					))
				)}

				{/* Both held back until the fetch lands: whether a retest can be requested is decided
				    from `retests`, so offering the action — or explaining why it is unavailable —
				    before the read returns would be a guess, and the wrong one whenever a retest
				    turns out to be open. */}
				{!loading && (
					<>
						{/* TableLink is the house "add a new row" affordance and has no disabled state, so
						    when a retest cannot be requested the reason takes the row rather than the row
						    silently disappearing — a missing action reads as "not for you", not "not yet". */}
						{unavailableReason ? (
							<tr>
								<td colSpan={COLUMNS} className='text-muted small'>{unavailableReason}</td>
							</tr>
						) : (
							<TableLink onClick={() => navigate(requestPath)}>
								Request Retest
							</TableLink>
						)}

						<tr>
							<td colSpan={COLUMNS} className='p-0'>
								<UsersPagination
									total={retests.length}
									page={page}
									pageSize={pageSize}
									onPageChange={setPage}
								/>
							</td>
						</tr>
					</>
				)}
			</tbody>
		</FadeTable>
	);
}
